import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Clock, Loader2, ShieldAlert } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useTranslation } from '@/hooks/useTranslation';
import { usePowensConnect } from '@/hooks/usePowensConnect';
import { Button } from '@/components/ui/button';
import { readLinkIntent } from '@/lib/powensLinkIntent';

type Status = 'syncing' | 'pending' | 'success' | 'sca' | 'error';

interface SyncResult {
  accounts_synced: number;
  connection_state: string | null;
  transfers_paired?: number | null; // Keep this line as it is
  /** Powens had not finished fetching from the bank; not a failure. */
  still_fetching?: boolean;
}

/**
 * Landing page for the Powens webview redirect.
 *
 * Powens sends the browser back here with ?connection_id=<id>. The id alone is
 * not data: the actual import runs in the powens-callback edge function, which
 * holds the user's permanent token.
 *
 * Reached without a connection id and without an error, the page syncs every
 * connection the user has at Powens. That recovers a link whose redirect never
 * made it back, and makes the URL usable as a plain "refresh my banks".
 */
export default function PowensCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { connect, isConnecting } = usePowensConnect();

  const [status, setStatus] = useState<Status>('syncing');
  const [result, setResult] = useState<SyncResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // The bank was connected to link a manual account: land back on that flow
  // rather than on the plain list, where the user would have to find it again.
  const linkAccountId = useMemo(() => readLinkIntent(), []);
  // The connection travels with it: the account picker must offer the accounts
  // of the bank just connected, and nothing else.
  const connectedId = searchParams.get('connection_id');
  const continuePath = linkAccountId
    ? `/accounts?link=${linkAccountId}${connectedId ? `&connection=${connectedId}` : ''}`
    : '/accounts';
  const continueLabel = linkAccountId ? t('accountLink.finish') : t('powens.backToAccounts');

  // The sync is not idempotent-free: StrictMode's double mount in development
  // would fire two full imports.
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const connectionId = searchParams.get('connection_id');
    const webviewError = searchParams.get('error') || searchParams.get('error_description');

    // The user closed the webview or the bank refused.
    if (webviewError) {
      setStatus('error');
      setErrorMessage(searchParams.get('error_description') || t('powens.cancelled'));
      return;
    }

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('powens-callback', {
          // No id means "sync everything this user has at Powens".
          body: connectionId ? { connection_id: Number(connectionId) } : {},
        });

        if (error) throw new Error(error.message);
        if (data?.error) throw new Error(data.error);

        if (data.connection_state) {
          setStatus('sca');
          return;
        }

        const sync = data as SyncResult;

        // Powens had not finished pulling from the bank yet. Saying "0 imported"
        // reads as a failure and inviting a retry is pointless — the webhook
        // will sync it the moment Powens is done.
        if (sync.still_fetching) {
          setStatus('pending');
          return;
        }

        // The count shown is the count this sync actually imported. It used to
        // be Math.max(synced, total Powens accounts on file), which turned a
        // sync that imported nothing into a cheerful "3 comptes importés" for
        // anyone who already had accounts — hiding exactly the failure the
        // screen exists to surface.
        setResult(sync);
        setStatus('success');
      } catch (err) {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Unknown error');
      }
    })();
  }, [searchParams, t]);

  return (
    <div className="min-h-screen bg-[#f9fafb] dark:bg-[#0f1117] flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl border border-gray-100 dark:border-gray-700/50 bg-white dark:bg-gray-800 p-8 text-center shadow-sm">
        {status === 'syncing' && (
          <>
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#16c47f] to-emerald-600 shadow-lg shadow-[#16c47f]/20">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('powens.syncing')}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {t('powens.syncingHint')}
            </p>
          </>
        )}

        {status === 'success' && result && (
          <>
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#16c47f] to-emerald-600 shadow-lg shadow-[#16c47f]/20">
              <CheckCircle2 className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('powens.successTitle')}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {result.accounts_synced} {t('powens.accountReady')}
            </p>
            <Button className="mt-6 w-full" onClick={() => navigate(continuePath)}>
              {continueLabel}
            </Button>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-400 to-blue-600 shadow-lg shadow-blue-500/20">
              <Clock className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('powens.pendingTitle')}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {t('powens.pendingDesc')}
            </p>
            <Button className="mt-6 w-full" onClick={() => navigate(continuePath)}>
              {continueLabel}
            </Button>
          </>
        )}

        {status === 'sca' && (
          <>
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/20">
              <ShieldAlert className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('powens.errorTitle')}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {t('powens.scaRequired')}
            </p>
            <Button className="mt-6 w-full" disabled={isConnecting} onClick={() => connect()}>
              {isConnecting ? t('powens.connecting') : t('powens.retry')}
            </Button>
            <button
              onClick={() => navigate('/accounts')}
              className="mt-3 w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {t('powens.backToAccounts')}
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-red-400 to-rose-600 shadow-lg shadow-rose-500/20">
              <AlertCircle className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('powens.errorTitle')}
            </h1>
            <p className="mt-2 break-words text-sm text-gray-500 dark:text-gray-400">
              {errorMessage}
            </p>
            <Button className="mt-6 w-full" disabled={isConnecting} onClick={() => connect()}>
              {isConnecting ? t('powens.connecting') : t('powens.retry')}
            </Button>
            <button
              onClick={() => navigate('/accounts')}
              className="mt-3 w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {t('powens.backToAccounts')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
